import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { memberService, MemberResponse } from '../services/memberService';
import { bodyAssessmentService } from '../services/bodyAssessmentService';
import { BodyAssessment } from '../types/BodyAssessment';
import { toast } from 'react-toastify';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface FieldDef { key: keyof BodyAssessment; label: string; unit: string; readOnly?: boolean }

const MEASUREMENT_FIELDS: FieldDef[] = [
  { key: 'weightKg', label: 'Weight', unit: 'kg' },
  { key: 'heightCm', label: 'Height', unit: 'cm' },
  { key: 'bodyFatPct', label: 'Body Fat %', unit: '%' },
  { key: 'muscleMassKg', label: 'Muscle Mass', unit: 'kg' },
  { key: 'waistCm', label: 'Waist', unit: 'cm' },
  { key: 'hipCm', label: 'Hip', unit: 'cm' },
  { key: 'chestCm', label: 'Chest', unit: 'cm' },
  { key: 'shoulderCm', label: 'Shoulders', unit: 'cm' },
  { key: 'leftBicepCm', label: 'Left Bicep', unit: 'cm' },
  { key: 'rightBicepCm', label: 'Right Bicep', unit: 'cm' },
  { key: 'leftThighCm', label: 'Left Thigh', unit: 'cm' },
  { key: 'rightThighCm', label: 'Right Thigh', unit: 'cm' },
  { key: 'leftCalfCm', label: 'Left Calf', unit: 'cm' },
  { key: 'rightCalfCm', label: 'Right Calf', unit: 'cm' },
  { key: 'bmi', label: 'BMI', unit: '', readOnly: true },
];

interface EditableField {
  key: keyof BodyAssessment;
  value: string;
}

const BodyMeasurementsPanel: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const numericMemberId = Number(memberId);
  const [member, setMember] = useState<MemberResponse | null>(null);
  const [memberError, setMemberError] = useState(false);

  useEffect(() => {
    let active = true;
    setMember(null);
    setMemberError(false);
    memberService.getMemberById(numericMemberId).then(response => {
      if (!active) return;
      if (response.success && response.data) setMember(response.data);
      else setMemberError(true);
    }).catch(() => { if (active) setMemberError(true); });
    return () => { active = false; };
  }, [numericMemberId]);

  const [latest, setLatest] = useState<BodyAssessment | null>(null);
  const [history, setHistory] = useState<BodyAssessment[]>([]);
  const [editing, setEditing] = useState<EditableField | null>(null);

  const [selectedMetric, setSelectedMetric] = useState<keyof BodyAssessment>('weightKg');

  const fetchData = async () => {
    if (!numericMemberId) return;
    const [latestRes, historyRes] = await Promise.all([
      bodyAssessmentService.getLatest(numericMemberId),
      bodyAssessmentService.getHistory(numericMemberId),
    ]);

    if (latestRes.success) setLatest(latestRes.data || null);
    else toast.error(latestRes.message);

    if (historyRes.success) setHistory(historyRes.data || []);
    else toast.error(historyRes.message);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const startEditing = (key: keyof BodyAssessment) => {
    const existingValue = latest ? latest[key]?.toString() || '' : '';
    setEditing({ key, value: existingValue });
  };

  const handleFieldSave = async () => {
    if (!editing || !numericMemberId) return;
    const fieldValue = editing.value.trim();

    const numericValue = fieldValue === '' ? undefined : Number(fieldValue);
    const existingRaw = latest ? (latest[editing.key] as number | null | undefined) : undefined;
    const unchanged = (
      numericValue == null && (existingRaw == null || Number.isNaN(existingRaw))
    ) || (
      numericValue != null && existingRaw != null && Math.abs(existingRaw - numericValue) < 0.0001
    );

    if (unchanged) {
      setEditing(null);
      return;
    }

    const payload: any = { [editing.key]: numericValue !== undefined ? numericValue : null };

    const res = await bodyAssessmentService.create(numericMemberId, payload);
    if (res.success && res.data) {
      toast.success('Measurement saved');
      setEditing(null);
      await fetchData();
    } else {
      toast.error(res.message);
    }
  };

  const chartData = useMemo(() => {
    const labels = history.map((h) => new Date(h.measuredAt).toLocaleDateString());
    const dataPoints = history.map((h) => (h[selectedMetric] ?? null) as number | null);

    return {
      labels,
      datasets: [
        {
          label: MEASUREMENT_FIELDS.find((f) => f.key === selectedMetric)?.label,
          data: dataPoints,
          borderColor: '#007bff',
          backgroundColor: 'rgba(0, 123, 255, 0.2)',
          spanGaps: false,
        },
      ],
    };
  }, [history, selectedMetric]);

  // Build history list for selected metric (descending date)
  const historyForSelected = useMemo(() => {
    return [...history]
      .sort((a, b) => new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime())
      .map((h) => ({
        date: new Date(h.measuredAt).toLocaleDateString(),
        value: h[selectedMetric] as number | null | undefined,
      }))
      .filter((h) => h.value !== null && h.value !== undefined);
  }, [history, selectedMetric]);

  if (!numericMemberId) {
    return <p>No member selected</p>;
  }

  const selectedField = MEASUREMENT_FIELDS.find((field) => field.key === selectedMetric);

  return (
    <div className="u-page measurements-page">
      <div className="u-page-header">
        <div><Link to="/members" className="u-back-link">← Members</Link><h2>Body measurements</h2><p>{member ? <><strong>{member.fullName}</strong> · Member {member.memberNumber}</> : memberError ? `Member record ${memberId} · Unable to load member details` : 'Loading member details…'}</p></div>
        <span className="u-badge u-badge-neutral">{history.length} assessments</span>
      </div>

      <div className="measurements-layout">
        <section className="u-panel measurements-metrics">
          <div className="u-section-heading"><div><h3>Current snapshot</h3><p>Select a metric to view its history. Click a value to edit.</p></div></div>
          <div className="measurement-list">
        {MEASUREMENT_FIELDS.map((f) => {
          const isEditing = !f.readOnly && editing?.key === f.key;
          const currentValue = latest ? (latest[f.key] as number | undefined) : undefined;
          return (
            <button
              type="button"
              key={f.key as string}
              className={`measurement-row ${selectedMetric === f.key ? 'active' : ''}`}
              onClick={() => setSelectedMetric(f.key)}
            >
              <span className="measurement-label">{f.label}</span>
              {isEditing ? (
                <input
                  type="number"
                  className="measurement-input"
                  value={editing?.value}
                  onChange={(e) => setEditing({ key: f.key, value: e.target.value })}
                  onBlur={handleFieldSave}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleFieldSave();
                  }}
                  autoFocus
                />
              ) : (
                <strong
                  onClick={(e) => {
                    if (f.readOnly) return; // no editing
                    e.stopPropagation();
                    startEditing(f.key);
                  }}
                >
                  {currentValue !== undefined && currentValue !== null ? `${currentValue} ${f.unit}` : '—'}
                </strong>
              )}
            </button>
          );
        })}
          </div>
        </section>

        <section className="u-panel measurements-detail">
          <div className="u-section-heading"><div><h3>{selectedField?.label} history</h3><p>Changes recorded across assessments.</p></div><span className="measurement-latest">Latest <strong>{historyForSelected[0]?.value ?? '—'} {selectedField?.unit}</strong></span></div>
          <div className="measurements-chart"><Line data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} /></div>
          <div className="measurement-history">
            <div className="measurement-history-header"><span>Date</span><span>Measurement</span></div>
            {historyForSelected.length === 0 ? <div className="u-state"><strong>No data yet</strong><span>New assessments will appear here.</span></div> : historyForSelected.map((item, index) => (
              <div className="measurement-history-row" key={`${item.date}-${index}`}><span>{item.date}</span><strong>{item.value} {selectedField?.unit}</strong></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BodyMeasurementsPanel; 
